import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { alpacaService } from '../../../api';
import { FetchAlpacaEventsRequest } from '../../../types/alpaca';
import { PaginatedRequest } from '../../../types/api';

export const fetchAlpacaEvents = createAsyncThunk('alpaca/fetch-events', (data: FetchAlpacaEventsRequest) =>
  alpacaService.fetchEvents(data),
);

export const fetchAlpacaOrders = createAsyncThunk(
  'alpaca/fetch-all-orders',
  (data: PaginatedRequest & { requestId: string }, { rejectWithValue }) =>
    alpacaService.fetchOrders(data).catch((error) => rejectWithValue(error)),
);

export const fetchAlpacaTransfers = createAsyncThunk(
  'alpaca/fetch-all-transfers',
  (data: PaginatedRequest & { requestId: string }, { rejectWithValue }) =>
    alpacaService.fetchTransfers(data).catch((error) => rejectWithValue(error)),
);

export const fetchOrdersByMadkholOrderId = createAsyncThunk(
  'alpaca/fetch-orders-by-madkhol-order-id',
  (madkholOrderId: string) => alpacaService.fetchOrdersByMadkholOrderId(madkholOrderId),
);

export const sendOrderToJournalQueue = createAsyncThunk(
  'alpaca/send-order-to-journal-queue',
  (payload: { madkholOrderId: string; useMock?: boolean }) =>
    alpacaService.sendOrderToJournalQueue(payload.madkholOrderId, payload.useMock),
);

export const sendOrderToCompleteQueue = createAsyncThunk(
  'alpaca/send-order-to-complete-queue',
  (payload: { madkholOrderId: string; useMock?: boolean }) =>
    alpacaService.sendOrderToCompleteQueue(payload.madkholOrderId, payload.useMock),
);

export const sendOrderToTransferQueue = createAsyncThunk(
  'alpaca/send-order-to-transfer-queue',
  (payload: { madkholOrderId: string; useMock?: boolean }) =>
    alpacaService.sendOrderToTransferQueue(payload.madkholOrderId, payload.useMock),
);

export const sendOrderToOrderQueue = createAsyncThunk(
  'alpaca/send-order-to-order-queue',
  (payload: { madkholOrderId: string; useMock?: boolean }) =>
    alpacaService.sendOrderToOrderQueue(payload.madkholOrderId, payload.useMock),
);

export const triggerTransferCreatedToCompleted = createAsyncThunk(
  'alpaca/trigger-transfer-created-to-completed',
  (madkholOrderId: string) => alpacaService.triggerTransferCreatedToCompleted(madkholOrderId),
);

export const triggerJournalStatus = createAsyncThunk(
  'alpaca/trigger-journal-status',
  (payload: { journalId: string; statusTo?: string; netAmount?: number }, { rejectWithValue }) =>
    alpacaService
      .triggerJournalStatus(payload.journalId, {
        statusTo: payload.statusTo,
        netAmount: payload.netAmount,
      })
      .catch((error) => rejectWithValue(error)),
);

export const triggerTransferStatus = createAsyncThunk(
  'alpaca/trigger-transfer-status',
  (payload: { transferId: number; statusTo?: string; amountUsd: number }, { rejectWithValue }) =>
    alpacaService.triggerTransferStatus(payload.transferId, payload).catch((error) => rejectWithValue(error)),
);

export const simulateNtaSubscription = createAsyncThunk(
  'alpaca/simulate-nta-subscription',
  (payload: { transferId: number; netAmountUsd: number }, { rejectWithValue }) =>
    alpacaService
      .simulateNtaSubscription(payload.transferId, { netAmountUsd: payload.netAmountUsd })
      .catch((error) => rejectWithValue(error)),
);

export const mockRedemptionBank = createAsyncThunk(
  'alpaca/mock-redemption-bank',
  (payload: { transferId: number; amountSar: number }, { rejectWithValue }) =>
    alpacaService.mockRedemptionBank(payload).catch((error) => rejectWithValue(error)),
);

export const triggerTradeEvent = createAsyncThunk(
  'alpaca/trigger-trade-event',
  (
    payload: {
      orderId: number;
      filledQty?: number;
      filledAvgPrice?: number;
      notionalUsd?: number;
    },
    { rejectWithValue },
  ) =>
    alpacaService
      .triggerTradeEvent(payload.orderId, {
        filledQty: payload.filledQty,
        filledAvgPrice: payload.filledAvgPrice,
        notionalUsd: payload.notionalUsd,
      })
      .catch((error) => rejectWithValue(error)),
);

export const fetchAccountByUserId = createAsyncThunk('alpaca/fetch-account-by-user-id', (userId: number) =>
  alpacaService.fetchAccountByUserId(userId),
);

export const createAlpacaAccount = createAsyncThunk('alpaca/create-account', (userId: number) =>
  alpacaService.createAccount(userId),
);

export const fetchTradingAccount = createAsyncThunk('alpaca/trading-account', () =>
  alpacaService.fetchTradingAccount(),
);

export const checkJournalStatus = createAsyncThunk(
  'alpaca/check-journal-status',
  (journalId: string, { rejectWithValue }) =>
    alpacaService.checkJournalStatus(journalId).catch((error) => rejectWithValue(error)),
);

export const cleanAlpacas = createAction('alpaca/clean');
